import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, DriftIcons, IconButton } from 'drift-ui';
import cx from 'classnames';
import RTSPro from '../assets/images/RTSPro.svg';
import RTSLogo from '../assets/images/RTS_white.svg';
import style from './Navigation.scss';
import { Menu } from './Menu';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/configureStore';
import { useTranslation } from 'react-i18next';
import { DO_APPLICATION_TYPE, getApplicationTitles } from 'client-experience-toolbox';
import ExitConfirmationModal from './ExitConfirmationModal';
import { getPreviousBankingSection } from 'utils/useFormNetworkCalls';
import { useApplication } from 'hooks/useApplication';
const { KeyboardArrowLeft } = DriftIcons;

interface NavigationProps {
  currentSection: number;
  totalSections: number;
  type: DO_APPLICATION_TYPE;
  customTitle?: string;
  customIcon?: React.ReactNode;
  hideMenuBar?: boolean;
  hideIcon?: boolean;
}

export const Navigation: FC<NavigationProps> = ({
  type,
  currentSection,
  totalSections,
  hideMenuBar,
  customTitle,
  customIcon,
  hideIcon,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const features = useSelector((state: RootState) => state?.persistedReducer?.activeFeatures || []);
  const isNewLogoEnabled = features.includes('7b6b33f6d1ca0f1d7416a24414c3cfc53f73957fbb3bf8a7ec62ea33c1b540d2');
  const [modalOpen, setModalOpen] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const { t } = useTranslation();
  const titles = getApplicationTitles(type);
  const title = currentSection > 0 ? t(titles[currentSection - 1]) : null;
  const { application } = useApplication(type);

  const exitDigitalOnboarding = () => {
    setExitModalOpen(false);

    // setTimeout to close the modal and then navigate on the next tick
    setTimeout(() => {
      switch (type) {
        case DO_APPLICATION_TYPE.FACTORING:
          navigate('/factoring');
          break;
        case DO_APPLICATION_TYPE.BANKING:
          navigate('/banking');
          break;
      }
    });
  };

  const navigatePreviousSection = () => {
    if (currentSection <= 1) {
      setExitModalOpen(true);
      return;
    }

    let previousSection = currentSection - 1;
    switch (type) {
      case DO_APPLICATION_TYPE.FACTORING:
        dispatch({ type: 'SET_FACTORING_SECTION', factoringSection: previousSection });
        break;
      case DO_APPLICATION_TYPE.BANKING:
        previousSection = getPreviousBankingSection(application, currentSection);
        dispatch({ type: 'SET_BANKING_SECTION', bankingSection: previousSection });
        break;
    }
  };

  const openMenu = () => {
    setModalOpen(true);
  };

  return (
    <nav>
      <ExitConfirmationModal
        open={exitModalOpen}
        onCancel={() => setExitModalOpen(false)}
        onConfirm={exitDigitalOnboarding}
        applicationType={type}
      />
      <Menu open={modalOpen} setOpen={setModalOpen} type={type} />
      <div className={cx(style.bannerContainer, isNewLogoEnabled && style.newBannerPadding)}>
        <div className={style.backAndLogo}>
          {!hideIcon ? (
            <IconButton color="inherit" onClick={navigatePreviousSection}>
              {customIcon || <KeyboardArrowLeft fontSize="large" color="inherit" />}
            </IconButton>
          ) : (
            <div className={style.emptyIconPlaceholder} />
          )}
          <img
            className={cx(style.logo, isNewLogoEnabled && style.newLogoHeight)}
            src={isNewLogoEnabled ? RTSLogo : RTSPro}
            alt={isNewLogoEnabled ? 'RTS Logo' : 'RTSPro Logo'}
          />
        </div>
        <div className={style.bannerTitle}>{customTitle || title}</div>
        <div className={style.lastColumn} />
      </div>
      {!hideMenuBar && (
        <div className={style.menuContainer}>
          <Button color="inherit" size="small" data-testid="menu-button" onClick={openMenu}>
            {t('Menu')}
          </Button>
          <div className={style.sectionIndicator}>
            {currentSection > 0 ? `${currentSection}/${totalSections}` : null}
          </div>
          <Button color="inherit" size="small" data-testid="close-button" onClick={() => setExitModalOpen(true)}>
            {t('Close')}
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
